interface WalkMeConfig {
  smartLoad: boolean;
}

interface WindowWithWalkMe extends Window {
  _walkmeConfig?: WalkMeConfig;
}

/**
 * Injects the WalkMe script into either the main document or a specified iframe
 * @param environment - The environment to load WalkMe for ('production' or 'test')
 * @param targetFrame - Optional iframe element to inject the script into
 */
export function injectWalkMeScript(environment: 'production' | 'test', targetFrame?: HTMLIFrameElement): void {
  const scriptUrl =
    environment === 'production'
      ? 'https://cdn.walkme.com/users/6f3c511ff42a4eca903c8d28fe70c4e6/walkme_6f3c511ff42a4eca903c8d28fe70c4e6_https.js'
      : 'https://cdn.walkme.com/users/6f3c511ff42a4eca903c8d28fe70c4e6/test/walkme_6f3c511ff42a4eca903c8d28fe70c4e6_https.js';

  // Determine target document (iframe or main window)
  const targetDocument = targetFrame?.contentDocument ?? document;
  const targetWindow = targetFrame?.contentWindow ?? (window as WindowWithWalkMe);

  if (targetDocument.querySelector(`script[src="${scriptUrl}"]`)) {
    return;
  }

  const walkme = targetDocument.createElement('script');
  walkme.type = 'text/javascript';
  walkme.async = true;
  walkme.src = scriptUrl;

  // Find the first script tag or append to head if none exists
  const scripts = targetDocument.getElementsByTagName('script');
  if (scripts.length > 0) {
    scripts[0].parentNode?.insertBefore(walkme, scripts[0]);
  } else {
    targetDocument.head.appendChild(walkme);
  }

  // @ts-expect-error -- walkme is a global variable
  targetWindow._walkmeConfig = { smartLoad: true };
}

/**
 * Helper function to check if an iframe is accessible
 * @param iframe - The iframe to check
 * @returns boolean indicating if the iframe can be accessed
 */
export function isIframeAccessible(iframe: HTMLIFrameElement): boolean {
  try {
    // Attempt to access the contentDocument
    const doc = iframe.contentDocument;
    return Boolean(doc);
  } catch {
    // If we get a security error, the iframe is cross-origin
    return false;
  }
}
